var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BufferGeometry, Box3, EdgesGeometry, LineBasicMaterial, LineSegments, Matrix4, Mesh, MeshBasicMaterial } from "three";
import { SimplifyModifier } from "three/examples/jsm/modifiers/SimplifyModifier.js";
import { mergeVertices } from "three/examples/jsm/utils/BufferGeometryUtils.js";
import { ModuleId, PrimitiveShape, isCompoundShape, isCollisionMeshShape } from "flavr-common";
import { busService } from "../../bus";
import { BVHBufferGeometry } from "../bvh/BVHBufferGeometry";
import { createColliderCompoundGeometry, createColliderNonPrimitiveGeometry, createColliderPrimitiveGeometry } from "@messages/selection/ColliderGeometryUtils";
import { getModelNameByUID } from "@modules/model";
import { configurePostprocessingOverlay } from "../../rendering/postprocessing/PostprocessingOverlay";
const SELECTABLE_DEBUG_MATERIAL = new MeshBasicMaterial({
    color: "red",
    transparent: true,
    opacity: 0
});
const TRIGGER_DEBUG_MATERIAL = new MeshBasicMaterial({
    color: "yellow",
    transparent: true,
    opacity: 0
});
const NOT_SELECTABLE_DEBUG_MATERIAL = new MeshBasicMaterial({
    color: "blue",
    transparent: true,
    opacity: 0
});
const SELECTABLE_OUTLINE_MATERIAL = new LineBasicMaterial({
    color: "red",
    transparent: true,
    opacity: 1
});
const TRIGGER_OUTLINE_MATERIAL = new LineBasicMaterial({
    color: "yellow",
    transparent: true,
    opacity: 1
});
const NOT_SELECTABLE_OUTLINE_MATERIAL = new LineBasicMaterial({
    color: "blue",
    transparent: true,
    opacity: 1
});
const OUTLINE_SIMPLIFICATION_MIN_VERTEX_COUNT = 256;
const OUTLINE_SIMPLIFICATION_RATIO = 0.2;
const OUTLINE_SIMPLIFICATION_MIN_RETAINED_VERTICES = 128;
const simplifyModifier = new SimplifyModifier();
/**
 * A collision shape is a simpler geometry used for ray casting and intersection tests. In order for it to be used in
 * ray casting it needs to be active. If a model should not be selectable but should still be used for intersection
 * tests, then isTrigger can be set to true (while active is set to false).
 */
export class CollisionShape extends Mesh {
    constructor() {
        super();
        this.uid = -1;
        this.shape = PrimitiveShape.Box;
        this.localTransform = new Matrix4();
        this.isSelectable = false;
        this.isTrigger = false;
        this.debugOutlineEnabled = false;
        this.worldBounds = new Box3();
        this.boundsMatrix = new Matrix4();
        this.boundsPositionVersion = -1;
        this.createOptimizedColliderGeometry = (shape) => __awaiter(this, void 0, void 0, function* () {
            let geometry;
            if (isCompoundShape(shape)) {
                geometry = yield createColliderCompoundGeometry(shape, this.connection, this.createOptimizedColliderGeometry);
            }
            else if (isCollisionMeshShape(shape)) {
                geometry = yield createColliderNonPrimitiveGeometry(shape, this.connection);
            }
            else {
                geometry = createColliderPrimitiveGeometry(shape);
            }
            if (geometry === undefined)
                return undefined;
            return new BVHBufferGeometry(geometry);
        });
        configurePostprocessingOverlay(this);
        this.connection = busService.connection(ModuleId.SELECTION);
    }
    create(uid_1, shape_1) {
        return __awaiter(this, arguments, void 0, function* (uid, shape, isSelectable = true, isTrigger = false) {
            this.uid = uid;
            this.name = getModelNameByUID(uid);
            this.shape = shape;
            this.changeSelectionState(isSelectable, isTrigger);
            const geometry = yield this.createOptimizedColliderGeometry(shape);
            if (geometry === undefined)
                throw new Error(`[selection] Unsupported collision shape ${String(shape)}`);
            this.geometry = geometry;
            this.material = SELECTABLE_DEBUG_MATERIAL;
            configurePostprocessingOverlay(this);
            return this;
        });
    }
    /**
     * Builds new geometry for `shape` without applying it. Building can take a while (mesh/hull data may need to be
     * fetched over the bus), and a newer request for this same shape can be made before this one finishes - so the
     * caller (CollisionWorld, which tracks a mutation version per uid) is responsible for checking whether this build
     * is still the most recent request before calling commitShape with the result, and disposing it instead if not.
     * Committing unconditionally here (as this used to) meant whichever concurrent build happened to finish *last*
     * silently won, regardless of which one was actually requested last - a stale, slower rebuild could overwrite a
     * newer, correct one.
     */
    buildShapeGeometry(shape) {
        return __awaiter(this, void 0, void 0, function* () {
            const geometry = yield this.createOptimizedColliderGeometry(shape);
            if (geometry === undefined)
                throw new Error(`[selection] Unsupported collision shape ${String(shape)}`);
            return geometry;
        });
    }
    /**
     * Applies geometry previously built by buildShapeGeometry - see buildShapeGeometry for why building and
     * committing are split.
     */
    commitShape(shape, geometry) {
        this.shape = shape;
        this.geometry.dispose();
        this.geometry = geometry;
        if (this.debugOutlineEnabled)
            this.rebuildOutline();
    }
    destroy() {
        this.disposeOutline();
        this.geometry.dispose();
    }
    /**
     * Returns cached world bounds. Geometry replacements/vertex edits invalidate local bounds;
     * movement only transforms the cached local box. Callers must not mutate the returned box.
     */
    getWorldBounds() {
        const position = this.geometry.getAttribute("position");
        const positionVersion = position === undefined ? -1 : ("version" in position ? position.version : position.data.version);
        const geometryChanged = this.boundsGeometry !== this.geometry ||
            this.boundsPosition !== position ||
            this.boundsPositionVersion !== positionVersion ||
            this.geometry.boundingBox === null;
        if (geometryChanged) {
            this.geometry.computeBoundingBox();
            this.boundsGeometry = this.geometry;
            this.boundsPosition = position;
            this.boundsPositionVersion = positionVersion;
        }
        if (geometryChanged || !this.boundsMatrix.equals(this.matrixWorld)) {
            this.boundsMatrix.copy(this.matrixWorld);
            const localBounds = this.geometry.boundingBox;
            if (localBounds === null) {
                this.worldBounds.makeEmpty();
            }
            else {
                this.worldBounds.copy(localBounds).applyMatrix4(this.matrixWorld);
            }
        }
        return this.worldBounds;
    }
    /**
     * A collision shape is active if its either selectable or a trigger or both
     */
    get isActive() {
        return this.isSelectable || this.isTrigger;
    }
    /**
     * By default, a collision shape needs to be selectable in order to be checked against during ray casting. This can
     * be changed if triggers are also included or if the user specifies uids for specific collision shapes to be
     * checked against.
     *
     * For intersection tests at least on of the options (isSelectable, isTrigger) need to be true to be considered. An
     * object can still be tested if the user specifically specifies the uid of the collision shape in the intersection
     * request.
     */
    changeSelectionState(isSelectable, isTrigger) {
        this.isSelectable = isSelectable;
        this.isTrigger = isTrigger;
        this.material = (isSelectable)
            ? SELECTABLE_DEBUG_MATERIAL
            : ((isTrigger)
                ? TRIGGER_DEBUG_MATERIAL
                : NOT_SELECTABLE_DEBUG_MATERIAL);
        if (this.outline !== undefined) {
            this.outline.material = (isSelectable)
                ? SELECTABLE_OUTLINE_MATERIAL
                : ((isTrigger)
                    ? TRIGGER_OUTLINE_MATERIAL
                    : NOT_SELECTABLE_OUTLINE_MATERIAL);
            this.outline.renderOrder = isSelectable ? 6 : (isTrigger ? 5 : 4);
        }
        this.renderOrder = isSelectable ? 3 : (isTrigger ? 2 : 1);
        configurePostprocessingOverlay(this);
    }
    setDebugOutlineEnabled(enabled) {
        if (this.debugOutlineEnabled === enabled)
            return;
        this.debugOutlineEnabled = enabled;
        if (enabled) {
            this.rebuildOutline();
        }
        else {
            this.disposeOutline();
        }
    }
    /// Creation helper ///
    rebuildOutline() {
        if (!this.debugOutlineEnabled)
            return;
        if (this.outline !== undefined) {
            this.remove(this.outline);
            this.outline.geometry.dispose();
        }
        const outlineSourceGeometry = this.createOutlineSourceGeometry();
        const outlineGeometry = new EdgesGeometry(outlineSourceGeometry);
        const outlineMaterial = this.isSelectable
            ? SELECTABLE_OUTLINE_MATERIAL
            : (this.isTrigger ? TRIGGER_OUTLINE_MATERIAL : NOT_SELECTABLE_OUTLINE_MATERIAL);
        this.outline = new LineSegments(outlineGeometry, outlineMaterial);
        configurePostprocessingOverlay(this.outline);
        this.outline.renderOrder = this.isSelectable ? 6 : (this.isTrigger ? 5 : 4);
        this.outline.frustumCulled = false;
        this.outline.raycast = (() => { });
        this.add(this.outline);
        if (outlineSourceGeometry !== this.geometry)
            outlineSourceGeometry.dispose();
    }
    disposeOutline() {
        if (this.outline === undefined)
            return;
        this.remove(this.outline);
        this.outline.geometry.dispose();
        this.outline = undefined;
    }
    createOutlineSourceGeometry() {
        var _a, _b, _c, _d;
        const vertexCount = (_b = (_a = this.geometry.getAttribute("position")) === null || _a === void 0 ? void 0 : _a.count) !== null && _b !== void 0 ? _b : 0;
        if (vertexCount < OUTLINE_SIMPLIFICATION_MIN_VERTEX_COUNT)
            return this.geometry;
        // SimplifyModifier only collapses vertices that match on every attribute, not just position. A flat-shaded
        // geometry like a Hull collider (built from ConvexGeometry) assigns each face its own normal, so two vertices
        // at a shared edge never match by normal even though they're at the same position - nothing gets welded and
        // nothing can be collapsed, regardless of whether the geometry already has an index. Strip everything but
        // position before welding so shared-position vertices always merge, then recompute normals on the result -
        // EdgesGeometry computes its own face normals from triangle positions, so the original (possibly flat-shaded)
        // normals aren't needed for outline purposes anyway.
        const positionOnlyGeometry = new BufferGeometry();
        positionOnlyGeometry.setAttribute("position", this.geometry.getAttribute("position"));
        if (this.geometry.index !== null)
            positionOnlyGeometry.setIndex(this.geometry.index);
        const weldedGeometry = mergeVertices(positionOnlyGeometry);
        weldedGeometry.computeVertexNormals();
        positionOnlyGeometry.dispose();
        const weldedVertexCount = (_d = (_c = weldedGeometry.getAttribute("position")) === null || _c === void 0 ? void 0 : _c.count) !== null && _d !== void 0 ? _d : 0;
        const removableVertexCount = Math.floor(weldedVertexCount * OUTLINE_SIMPLIFICATION_RATIO);
        const maxRemovableVertices = Math.max(weldedVertexCount - OUTLINE_SIMPLIFICATION_MIN_RETAINED_VERTICES, 0);
        const simplificationCount = Math.min(removableVertexCount, maxRemovableVertices);
        if (simplificationCount <= 0)
            return weldedGeometry;
        try {
            const simplifiedGeometry = simplifyModifier.modify(weldedGeometry, simplificationCount);
            weldedGeometry.dispose();
            return simplifiedGeometry;
        }
        catch (error) {
            console.warn(`[selection] SimplifyModifier threw for uid ${this.uid} (${this.name}) - falling back to welded geometry`, error);
            return weldedGeometry;
        }
    }
}
